import { useBundle } from '@amzn/react-arb-tools';
import {
  pathToCssVarName,
  PREFERS_COLOR_SCHEME_ID,
  token,
} from '@amzn/stencil-design-tokens/js/web/utils';
import { MainWithSkipLink } from '@amzn/stencil-react-components/a11y';
import { ThemeMode } from '@amzn/stencil-react-components/context';
import {
  AppLayout,
  AppLayoutHeader,
  Col,
  Row,
  Spacer,
} from '@amzn/stencil-react-components/layout';
import {
  Link,
  LinkTextDecorationOptions,
} from '@amzn/stencil-react-components/link';
import {
  PageHeader,
  PageHeaderDropdown,
  PageHeaderDropdownLink,
  PageHeaderLink,
  PageHeaderList,
} from '@amzn/stencil-react-components/page';
import { useBreakpoints } from '@amzn/stencil-react-components/responsive';
import { Text } from '@amzn/stencil-react-components/text';
import React, { PropsWithChildren } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackClick } from 'src/analytics/clickTracking';
import {
  CAREERS_LINK,
  CONDITIONS_OF_USE_LINK,
  NAV_MESSAGE_BUNDLE_NAME,
  PRIVACY_NOTICE_LINK,
} from 'src/constants';
import { NAV_LAYOUT_PAGE_GROUP } from 'src/constants/MonitoringConstants';
import { useSPANavigationFocus } from 'src/hooks/useSPANavigationFocus';
import { navigateToSection } from 'src/utils/redirectUtils';
import { t } from '../helpers';
import amazonLogoWhiteText from '../images/amazon-white-text.png';
import gtcLogoWhite from '../images/gtc-in-this-together-white.svg';

const NAV_ITEMS = [
  { id: 'grow', ref: '#grow', text: 'grow_nav' },
  { id: 'thrive', ref: '#thrive', text: 'thrive_nav' },
  { id: 'connect', ref: '#connect', text: 'connect_nav' },
  { id: 'careers', ref: CAREERS_LINK, text: 'career_nav' },
];

const GrowThriveConnectLayout = ({ children }: PropsWithChildren) => {
  const [bundle] = useBundle(NAV_MESSAGE_BUNDLE_NAME);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isHomePage = pathname === '/';

  useSPANavigationFocus();

  const { matches } = useBreakpoints();

  const hasHeaderLogos = matches.xl || matches.l;

  const navItemProps = (nav: (typeof NAV_ITEMS)[number]) => {
    const isSectionRef = nav.ref.startsWith('#');

    return {
      href: isSectionRef && !isHomePage ? `/${nav.ref}` : nav.ref,
      ...(!isSectionRef && {
        target: '_blank',
        rel: 'noopener noreferrer',
        'aria-label': t(bundle, 'opens_in_new_tab', {
          label: t(bundle, nav.text),
        }),
      }),
      onClick: (e: React.MouseEvent) => {
        if (isSectionRef) {
          e.preventDefault();
          if (isHomePage) {
            navigateToSection(nav.ref);
          } else {
            navigate({ pathname: '/', hash: nav.ref });
          }
        }
        trackClick({
          elementId: nav.id,
          elementType: 'nav',
          pageGroup: NAV_LAYOUT_PAGE_GROUP,
          metadata: { href: nav.ref },
        });
      },
    };
  };

  function footerWrapper(disclaimers: React.ReactNode[]): React.ReactNode {
    return matches.m || matches.s ? (
      <Col width={'100%'} alignItems="center" dataTestId="footer-small-test-id">
        {disclaimers.map((text, index) => {
          return (
            <Col
              key={index}
              width={'100%'}
              gridGap={'dimensions.spacing.400'}
              alignItems="center"
              padding={{
                left: 'dimensions.spacing.500',
                right: 'dimensions.spacing.500',
                top: 'dimensions.spacing.200',
                bottom: 'dimensions.spacing.200',
              }}
            >
              {text}
            </Col>
          );
        })}
      </Col>
    ) : (
      <Row width={'100%'} dataTestId="footer-large-test-id">
        <Row
          width={'100%'}
          gridGap={'dimensions.spacing.400'}
          justifyContent="flex-start"
          padding={{
            left: 'dimensions.spacing.500',
            right: 'dimensions.spacing.500',
            top: 'dimensions.spacing.400',
            bottom: 'dimensions.spacing.400',
          }}
        >
          {disclaimers}
        </Row>
      </Row>
    );
  }

  return (
    <AppLayout isHeaderFixed={true}>
      <Col minHeight="100dvh">
        <AppLayoutHeader
          dataTestId="app-layout-header-test-id"
          {...{ [PREFERS_COLOR_SCHEME_ID]: ThemeMode.Dark }}
          style={{ [pathToCssVarName('color.border.primary')]: 'transparent' }}
        >
          <PageHeader>
            {hasHeaderLogos && (
              <img
                data-test-id="gtc-logo-test-id"
                src={gtcLogoWhite}
                style={{
                  width: 'auto',
                  height: '2.5rem',
                  marginRight: token('dimensions.spacing.400'),
                  alignSelf: 'center',
                }}
                alt={t(bundle, 'gtc_logo_alt')}
                loading="lazy"
                aria-hidden="true"
              />
            )}
            {hasHeaderLogos ? (
              <PageHeaderList
                dataTestId="nav-list-test-id"
                items={NAV_ITEMS.map((nav) => (
                  <PageHeaderLink key={nav.ref} {...navItemProps(nav)}>
                    {t(bundle, nav.text)}
                  </PageHeaderLink>
                ))}
              />
            ) : (
              <PageHeaderDropdown
                dataTestId="nav-dropdown-test-id"
                aria-label={t(bundle, 'nav_menu')}
                items={NAV_ITEMS.map((nav) => (
                  <PageHeaderDropdownLink key={nav.ref} {...navItemProps(nav)}>
                    {t(bundle, nav.text)}
                  </PageHeaderDropdownLink>
                ))}
              />
            )}
            <Spacer flex={1} />
            {hasHeaderLogos && (
              <img
                data-test-id="amazon-logo-test-id"
                src={amazonLogoWhiteText}
                style={{
                  width: 'auto',
                  height: '1.875rem',
                  alignSelf: 'center',
                }}
                alt={t(bundle, 'amazon_logo_alt')}
                loading="lazy"
                aria-hidden="true"
              />
            )}
          </PageHeader>
        </AppLayoutHeader>
        <Col flex="1" backgroundColor="color.surface.dark-bg-static">
          <Col flex="1">
            <MainWithSkipLink>{children}</MainWithSkipLink>
          </Col>
          <Col width={'100%'} role="contentinfo">
            <Col
              {...{ [PREFERS_COLOR_SCHEME_ID]: ThemeMode.Dark }}
              backgroundColor={'color.surface.dark-bg-static'}
              width={'100%'}
              gridGap="dimensions.spacing.100"
              alignItems="center"
            >
              {(matches.m || matches.s) && (
                <Row
                  dataTestId="logo-footer-test-id"
                  padding={'dimensions.spacing.400'}
                >
                  <img
                    src={amazonLogoWhiteText}
                    style={{
                      maxWidth: '5.625rem',
                      maxHeight: '1.875rem',
                      width: 'auto',
                      height: 'auto',
                    }}
                    alt={t(bundle, 'amazon_logo_alt')}
                    loading="lazy"
                    aria-hidden="true"
                  />
                </Row>
              )}

              {footerWrapper([
                <Link
                  key="footer-conditions-of-use"
                  href={CONDITIONS_OF_USE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t(bundle, 'opens_in_new_tab', {
                    label: t(bundle, 'footer_conditions_of_use'),
                  })}
                  textDecoration={LinkTextDecorationOptions.Underline}
                  fontSize={'text.size.100'}
                  onClick={() =>
                    trackClick({
                      elementId: 'conditions-of-use',
                      elementType: 'link',
                      pageGroup: NAV_LAYOUT_PAGE_GROUP,
                      metadata: { href: CONDITIONS_OF_USE_LINK },
                    })
                  }
                >
                  {t(bundle, 'footer_conditions_of_use')}
                </Link>,
                <Link
                  key="footer-privacy-notice"
                  href={PRIVACY_NOTICE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t(bundle, 'opens_in_new_tab', {
                    label: t(bundle, 'footer_privacy_notice'),
                  })}
                  textDecoration={LinkTextDecorationOptions.Underline}
                  fontSize={'text.size.100'}
                  onClick={() =>
                    trackClick({
                      elementId: 'privacy-notice',
                      elementType: 'link',
                      pageGroup: NAV_LAYOUT_PAGE_GROUP,
                      metadata: { href: PRIVACY_NOTICE_LINK },
                    })
                  }
                >
                  {t(bundle, 'footer_privacy_notice')}
                </Link>,
                <Text
                  key="footer-affiliates"
                  color="color.text.primary"
                  fontSize={'text.size.100'}
                >
                  {t(bundle, 'footer_affiliates')}
                </Text>,
              ])}
            </Col>
          </Col>
        </Col>
      </Col>
    </AppLayout>
  );
};

export default GrowThriveConnectLayout;
